import { Boundary } from "../models/Boundary";
import { BoundaryType } from "../models/BoundaryType";
import { BoundaryTypeKey } from "../models/BoundaryTypeKey";
import { GeoJSONPolygonType } from "../models/GeoJSONPolygonType";
import { AreaInput, AreaTypeKey, GeoJSONGeometryType } from "../models/globalTypes";
import { Project } from "../models/Project";
import { removeTypenameDeep } from "./apollo";
import { createMapItem, ensureGeographic, MapItemPopup, SimpleFillStyle } from "./arcgisUtils";

const red = { r: 255, g: 0, b: 0 } as __esri.Color;
const green = { r: 119, g: 226, b: 40 } as __esri.Color;
const orange = { r: 226, g: 119, b: 40 } as __esri.Color;

const getColor = (typeKey: AreaTypeKey) => {
  switch (typeKey) {
    case AreaTypeKey.PERMIT_BOUNDARY:
      return orange;
    case AreaTypeKey.ANNOTATION_BOUNDARY:
      return red;
    default:
      return orange;
  }
};

const getStyle = (typeKey: AreaTypeKey): SimpleFillStyle => {
  switch (typeKey) {
    case AreaTypeKey.PERMIT_BOUNDARY:
      return "solid";
    case AreaTypeKey.ANNOTATION_BOUNDARY:
      return "backward-diagonal";
    default:
      return "solid";
  }
};

const createPermitMapItem = (
  permitId: string,
  permitNumber: number,
  permitLocationOfWork: string,
  boundary: Boundary
) => {
  return createMapItem(
    "polygon",
    boundary.polygon.coordinates as number[][][],
    {
      id: permitId,
      name: "View permit",
      title: "Permit number: " + permitNumber,
      content: "Location of work: " + permitLocationOfWork,
      hasAction: true,
    } as MapItemPopup,
    undefined,
    getColor(boundary.type.key),
    getStyle(boundary.type.key)
  );
};

const createBoundryMapItem = (id: string, withPopup: boolean, hasAction: boolean, boundary: Boundary) => {
  const { polygon, ...rest } = boundary;
  return createMapItem(
    "polygon",
    polygon.coordinates as number[][][],
    withPopup
      ? ({
          id: id,
          name: "Edit",
          title: "Type: " + boundary.type?.name,
          content: "Comment: " + (boundary.comments || ""),
          hasAction: hasAction,
          actionType: "edit",
        } as MapItemPopup)
      : undefined,
    rest,
    getColor(boundary.type.key),
    getStyle(boundary.type.key)
  );
};

const createBoundaryFromGraphic = (graphic: __esri.Graphic) => {
  return {
    type: graphic.attributes?.type ? (graphic.attributes?.type as BoundaryType) : BoundaryTypeKey.PermitBoundary,
    polygon: createGeoJsonPolygon(ensureGeographic(graphic.geometry as __esri.Polygon)),
    comments: graphic.attributes ? (graphic.attributes.comments as string) : "",
  } as Boundary;
};

const createGeoJsonPolygon = (polygon: __esri.Geometry): GeoJSONPolygonType => {
  return {
    type: GeoJSONGeometryType.Polygon,
    coordinates: (polygon as __esri.Polygon).rings,
    crs: polygon.spatialReference.wkid,
  } as GeoJSONPolygonType;
};

const createProjectMapItem = (project: Project) => {
  return createMapItem(
    "polyline",
    project.boundaries?.coordinates as number[][][],
    {
      id: project.id,
      name: "View project",
      title: `Project: ${project.name} ${project.number ? "(" + project.number + ")" : ""}`,
      content: "Location of work: " + project.location,
      hasAction: false,
    } as MapItemPopup,
    undefined,
    green
  );
};

const mapBoundaryToAreaInput = (boundary: Boundary) => {
  return {
    typeKey: boundary.type.key,
    polygon: removeTypenameDeep(boundary.polygon),
    comments: boundary.comments,
  } as AreaInput;
};

const mapGeoJSONPolygonTypeToBoundary = (polygon: GeoJSONPolygonType) => {
  return { type: BoundaryTypeKey.PermitBoundary, polygon: polygon } as Boundary;
};

export {
  createPermitMapItem,
  createBoundryMapItem,
  createBoundaryFromGraphic,
  createGeoJsonPolygon,
  createProjectMapItem,
  mapBoundaryToAreaInput,
  mapGeoJSONPolygonTypeToBoundary,
};
